import styled from '@emotion/styled'

import { Flex, PaddingInset, spacing, Text, Timeline } from '@cash-design-system/react'
import {
  FormBlockerElementTimelineElement,
  FormBlockerElementTimelineElementEvent,
  FormBlockerElementTimelineElementEventIcon,
  FormBlockerElementTimelineElementEventInlineTextFormat,
} from '@cash-web/protos/squareup/franklin/form_blocker.pb'
import logger from '@cash-web/shared/util-logger'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { BORDER_WIDTH, CIRCLE_SIZE, TimelineEventIcon } from './TimelineEventIcon'

const TimelineEventWrapper = styled.div`
  display: grid;
  grid-template-columns: 2rem 1fr;
  position: relative;
`

const Line = styled.div<{ lineColor: 'iconStandard' | 'iconExtraSubtle' }>`
  position: absolute;
  background: ${({ theme, lineColor }) => theme.arcadeColors.semantic[lineColor]};
  width: ${BORDER_WIDTH};
  height: calc(100% + ${BORDER_WIDTH});
  top: calc(${CIRCLE_SIZE} - (${BORDER_WIDTH} / 2));
  left: calc((${CIRCLE_SIZE} / 2) - (${BORDER_WIDTH} / 2));
`

const DetailTextWrapper = styled.span`
  text-align: right;
  flex: 50%;
`

const TimelineEvent = ({
  event,
  shouldRenderLine,
  lineColor,
}: {
  event: FormBlockerElementTimelineElementEvent
  shouldRenderLine: boolean
  lineColor: 'iconStandard' | 'iconExtraSubtle'
}) => {
  const { icon, title, detail_text, inline_description_text, inline_description_text_format } = event

  const inlineDescriptionColor =
    inline_description_text_format === FormBlockerElementTimelineElementEventInlineTextFormat.SECONDARY
      ? 'textSubtle'
      : 'textStandard'

  return (
    <TimelineEventWrapper>
      {shouldRenderLine && <Line lineColor={lineColor} />}
      {icon && <TimelineEventIcon icon={icon} />}
      <Flex flexDirection="column" gap={spacing.xs}>
        <Flex gap={spacing.xs}>
          <Text typography="labelMedium" color="textStandard">
            {title}
          </Text>
          <DetailTextWrapper>
            <Text typography="labelMedium" color="textStandard">
              {detail_text}
            </Text>
          </DetailTextWrapper>
        </Flex>
        <Text color={inlineDescriptionColor} typography="body">
          {inline_description_text}
        </Text>
      </Flex>
    </TimelineEventWrapper>
  )
}

const getLineColor = (index: number, events: FormBlockerElementTimelineElementEvent[]) => {
  const currentEvent = events[index]
  const nextEvent = events[index + 1]

  const isEventPrimary = (event: FormBlockerElementTimelineElementEvent) => {
    return (
      event.icon === FormBlockerElementTimelineElementEventIcon.HIGHLIGHT ||
      event.icon === FormBlockerElementTimelineElementEventIcon.HIGHLIGHT_COMPLETED
    )
  }

  if (nextEvent && isEventPrimary(currentEvent) && isEventPrimary(nextEvent)) {
    return 'iconStandard'
  }
  return 'iconExtraSubtle'
}

const getStatusFromIcon = ({ icon }: { icon: FormBlockerElementTimelineElementEventIcon | undefined }) => {
  if (
    icon === FormBlockerElementTimelineElementEventIcon.COMPLETED ||
    icon === FormBlockerElementTimelineElementEventIcon.HIGHLIGHT_COMPLETED
  ) {
    return 'DONE'
  } else if (
    icon === FormBlockerElementTimelineElementEventIcon.SKIPPED ||
    icon === FormBlockerElementTimelineElementEventIcon.REFUNDED
  ) {
    return 'BYPASSED'
  } else if (
    icon === FormBlockerElementTimelineElementEventIcon.MISSED ||
    icon === FormBlockerElementTimelineElementEventIcon.FAILED
  ) {
    return 'FAILED'
  } else if (icon === FormBlockerElementTimelineElementEventIcon.NORMAL) {
    return 'INCOMPLETE'
  } else if (icon === FormBlockerElementTimelineElementEventIcon.ALERT) {
    return 'ALERT'
  }
  return 'PENDING'
}

export const TimelineElement = ({ events }: FormBlockerElementTimelineElement) => {
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')

  if (!events) {
    logger.error(new Error('FormBlockerElementTimelineElement does not contain Events'))
    return
  }

  if (isArcadeMigrationEnabled)
    return (
      <Timeline
        events={events.map(event => {
          return {
            title: event.title,
            status: getStatusFromIcon({ icon: event.icon }),
            value: event.detail_text,
            body: event.inline_description_text,
            icon: event.icon ? <TimelineEventIcon icon={event.icon} /> : undefined,
          }
        })}
      />
    )
  return (
    <PaddingInset data-testid="padding-inset">
      <Flex flexDirection="column" gap={spacing.l}>
        {events?.map((event, index) => {
          if (event.icon === FormBlockerElementTimelineElementEventIcon.CANCELED) {
            logger.error(new Error('Canceled TimelineEvent is not supported'))
            return null
          }
          return (
            <TimelineEvent
              key={index}
              event={event}
              shouldRenderLine={index !== events.length - 1}
              lineColor={getLineColor(index, events)}
            />
          )
        })}
      </Flex>
    </PaddingInset>
  )
}
